/*
 * Core
 * */
import cn from "classnames";
import { lazy, useEffect, useRef, useState } from "react";
import ReCAPTCHA from "react-google-recaptcha";
import { useTranslation } from "react-i18next";

import InputGlobal from "shared/components/Form/InputGlobal";
import { Textarea } from "shared/components/Form/Textarea";
import SpinLoader, {
  LOADER_COLOR_DARK,
  LOADER_SIZE_LARGE,
  POSITION_ABSOLUTE,
} from "shared/components/SpinLoader";

import {
  ERROR_STATUS_UNPROCESSABLE,
  MAX_LENGTH_TEXTAREA,
} from "shared/constants";
import {
  getErrorEventEmail,
  getErrorEventsName,
} from "shared/constants/auth.js";
import { DESPUTE_TOO_LONG_DESCRIPTION } from "shared/constants/errors";

import { simplyClick } from "shared/helpers/analytics.js";
import { clearForm } from "shared/helpers/auth";
import { validateEmail, validateName } from "shared/helpers/validation";

import { sendContactUs } from "shared/services/auth";

/*
 * Assets
 * */
import successAnimation from "assets/shared/lotties/success-mark.json";

const Lottie = lazy(() => import("lottie-react"));

export const ContactUs = ({ isReviewsForm }) => {
  const [contactUsData, setContactUsData] = useState({});
  const [errorName, setErrorName] = useState(null);
  const [errorEmail, setErrorEmail] = useState(null);
  const [captchaError, setCaptchaError] = useState(null);
  const [errorDescription, setErrorDescription] = useState(null);
  const [responseError, setResponseError] = useState(null);
  const [successContactUs, setSuccessContactUs] = useState(false);
  const [loader, setLoader] = useState(false);
  const [messageLength, setMessageLength] = useState(0);

  const { t } = useTranslation("auth");
  const recaptchaRef = useRef(null);

  /*
   * Styles for animation wrap
   * */
  const style = {
    height: 86,
    width: 136,
    marginLeft: "auto",
    marginRight: "auto",
  };

  /*
   * Effect for clean Contact Us Modal
   */
  useEffect(() => {
    clearForm(".close__icon.contact", ".background__modal", clearContactUsForm);
  }, []);

  useEffect(() => {
    const handleKeyDown = (e) => {
      if (e.key === "Escape") {
        const contactUsModal = document.querySelector(".contact__us-modal");
        contactUsModal.classList.remove("show");
        document.body.style.overflow = "unset";
      }
    };

    document.addEventListener("keydown", handleKeyDown);

    return () => {
      document.removeEventListener("keydown", handleKeyDown);
    };
  }, []);

  /*
   * Effect for fixed min height success modal
   */
  useEffect(() => {
    const successWrap = document.querySelector(".contact_us-wrap");
    if (successContactUs && successWrap) {
      successWrap.classList.add("fixed");
    } else {
      successWrap.classList.remove("fixed");
    }
  }, [successContactUs]);

  /*
   * Clear Contact Us Form
   */
  const clearContactUsForm = () => {
    recaptchaRef.current?.reset();
    setContactUsData({});
    setErrorName(null);
    setErrorEmail(null);
    setCaptchaError(null);
    setErrorDescription(null);
    setResponseError(null);
    setSuccessContactUs((prev) => {
      if (!prev) {
        simplyClick("cl_home_p_contact_cross");
      } else {
        simplyClick("cl_home_p_contact_success_popup_cross");
      }
      return false;
    });
    handleCloseSuccessModal();
  };

  /**
   * Close Success Modal
   */
  const handleCloseSuccessModal = (name) => {
    const contactUsModal = document.querySelector(".contact__us-modal");

    if (!contactUsModal) {
      return;
    }

    contactUsModal.classList.remove("show");
    document.body.style.overflow = "auto";

    if (name === "buttonOk") {
      simplyClick("cl_home_p_contact_success_popup_ok");
    }

    setSuccessContactUs(false);
    setContactUsData({});
  };

  /**
   * Handler for change Name
   * @param {object} event
   */
  const handleChangeName = (event) => {
    setErrorName(null);
    setContactUsData({ ...contactUsData, name: event?.target?.value });
  };

  /**
   * Handle change Email
   * @param {object} event
   */
  const handleChangeEmail = (event) => {
    setErrorEmail(null);
    setContactUsData({ ...contactUsData, email: event?.target?.value });
  };

  /**
   * Handle change description
   * @param {string} descriptionValue
   */
  const handleDescription = (descriptionValue) => {
    setErrorDescription(null);
    setContactUsData({ ...contactUsData, text: descriptionValue });

    if (isReviewsForm) {
      setMessageLength(descriptionValue.length);
    }
  };

  /**
   * Handle change Google Captcha
   * @param {string} value recaptcha
   */
  const onCaptchaChange = (value) => {
    setCaptchaError(null);
    setContactUsData({ ...contactUsData, reCaptcha: value });
    simplyClick("cl_home_p_contact_captcha_mark");
  };

  /**
   * Handle submit form and request on api
   */
  const handleRequestContactUs = (e) => {
    e.preventDefault();

    let isValidateDataForm = true;

    if (isReviewsForm) {
      simplyClick("cl_review_page_send");
    } else {
      simplyClick("cl_home_p_contact_send");
    }

    const { error, status = { text: "", type: "" } } = validateName(
      contactUsData?.name ?? ""
    );

    if (!status) {
      setErrorName(error.text);
      isValidateDataForm = false;
    }

    if (error.type) {
      simplyClick(getErrorEventsName(error.type, "p_contact", "home"));
    }

    const resultEmail = validateEmail(contactUsData.email);

    if (!resultEmail.status) {
      simplyClick(getErrorEventEmail(resultEmail.error, "contact", "home_p"));
      setErrorEmail(resultEmail.error);
      isValidateDataForm = false;
    }

    if (
      !contactUsData.text ||
      contactUsData.text.trim().length === 0 ||
      contactUsData.text.trim() === "undefined"
    ) {
      setErrorDescription(isReviewsForm ? t("fieldRequired") : t("emptyError"));
      simplyClick("err_home_p_contact_message_no");
      isValidateDataForm = false;
    } else if (contactUsData.text.length > MAX_LENGTH_TEXTAREA) {
      setErrorDescription(DESPUTE_TOO_LONG_DESCRIPTION);
      isValidateDataForm = false;
    }

    if (!contactUsData.reCaptcha) {
      setCaptchaError(t("captchaError"));
      simplyClick("err_home_p_contact_captcha_no");
      isValidateDataForm = false;
    }

    if (isValidateDataForm) {
      setLoader(true);
      successCbContactUs(contactUsData, () => {
        setLoader(false);
        setSuccessContactUs(true);
        simplyClick("sh_home_p_contact_success_popup");
      });
    }
  };

  /**
   * Request Contact Us on api
   * @param {object} dataReset
   * @param {function|null} cbSuccess
   */
  const successCbContactUs = (dataReset, cbSuccess = null) => {
    sendContactUs(dataReset)
      .then(({ data }) => {
        if (data?.success) {
          setLoader(false);
          if (cbSuccess) {
            cbSuccess();
          }
        }
      })
      .catch(({ response }) => {
        setLoader(false);
        recaptchaRef.current?.reset();
        setContactUsData({ ...contactUsData, reCaptcha: "" });
        if (response.status === ERROR_STATUS_UNPROCESSABLE) {
          const { data } = response;
          let errorText = "";
          data.errors.forEach((error) => {
            if (error.name === "name") {
              setErrorName(error.message);
            } else if (error.name === "email") {
              setErrorEmail(error.message);
            } else if (error.name === "text") {
              setErrorDescription(error.message);
            } else if (error.name === "reCaptcha") {
              recaptchaRef.current?.reset();
              setCaptchaError(error.message);
            } else {
              errorText +=
                errorText.length > 0 ? " " + error.message : error.message;
            }
          });
          if (errorText.length !== 0) {
            setResponseError(errorText);
          }
        } else {
          setResponseError(t("common:error.canUpdate"));
        }
      });
  };

  return (
    <>
      {loader && (
        <SpinLoader
          color={LOADER_COLOR_DARK}
          size={LOADER_SIZE_LARGE}
          position={POSITION_ABSOLUTE}
          withBlur={true}
        />
      )}
      {successContactUs ? (
        <>
          <div className="contact_us-body-success">
            <Lottie
              loop={false}
              style={style}
              animationData={successAnimation}
              autoplay={true}
              rendererSettings={{
                preserveAspectRatio: "xMidYMid slice",
              }}
            />
            <span className="title">{t("successModalTitle")}</span>
            <p className="text">
              {isReviewsForm
                ? t("reviewsSuccessModalText")
                : t("successModalText")}
            </p>
            <button
              onClick={() => handleCloseSuccessModal("buttonOk")}
              className="form__button"
            >
              OK
            </button>
          </div>
        </>
      ) : (
        <form noValidate onSubmit={handleRequestContactUs}>
          <div className="contact_us-body">
            <div className="contact_us-header">
              <span className="title">
                {isReviewsForm ? t("reviewModalTitle") : t("modalTitle")}
              </span>
            </div>

            {isReviewsForm && <span className="line"></span>}

            <span className="label_global">
              {isReviewsForm ? t("reviewLabelName") : t("labelName")}
            </span>

            <InputGlobal
              className={"register__form"}
              handleChange={(e) => handleChangeName(e)}
              name="name"
              value={contactUsData.name ?? ""}
              error={!!errorName}
              errorText={errorName}
              placeholder={
                isReviewsForm
                  ? t("reviewPalceholderName")
                  : t("placeholderName")
              }
              onFocus={() => simplyClick("cl_home_p_contact_name_type")}
            />

            <span className="label_global">
              {isReviewsForm ? t("reviewLabelEmail") : t("labelEmail")}
            </span>

            <InputGlobal
              className={"register__email"}
              handleChange={(e) => handleChangeEmail(e)}
              name="email"
              value={contactUsData.email ?? ""}
              error={!!errorEmail}
              errorText={errorEmail}
              placeholder={
                isReviewsForm
                  ? t("reviewPlaceholderEmail")
                  : t("placeholderEmail")
              }
              onFocus={() => simplyClick("cl_home_p_contact_email_type")}
            />

            {!isReviewsForm && (
              <span className="label_global">{t("messageLabel")}</span>
            )}

            <div className="textarea__wrap">
              <Textarea
                name={"description"}
                value={contactUsData.text ?? ""}
                handleChange={handleDescription}
                rows={5}
                textareaId={"description"}
                placeholder={
                  isReviewsForm
                    ? t("reviewPlaceholder")
                    : t("messagePlaceholder")
                }
                customClass={cn({ error_textarea: errorDescription })}
                error={!!errorDescription}
                onFocus={() => simplyClick("cl_home_p_contact_message_type")}
              />

              <div
                className={cn("review__error-block", {
                  error: errorDescription,
                })}
              >
                {errorDescription && (
                  <p className="error">{errorDescription}</p>
                )}

                {isReviewsForm && (
                  <div
                    className={cn("review__text-length", {
                      error:
                        errorDescription || messageLength > MAX_LENGTH_TEXTAREA,
                    })}
                  >
                    {messageLength}/{MAX_LENGTH_TEXTAREA}
                  </div>
                )}
              </div>
            </div>

            {isReviewsForm && <span className="line"></span>}

            <div className={cn({ "recaptcha-error": captchaError })}>
              <div className="g-recaptcha">
                <ReCAPTCHA
                  sitekey={import.meta.env.VITE_APP_RECAPTCHA_TOKEN}
                  onChange={onCaptchaChange}
                  ref={recaptchaRef}
                />
              </div>
              {captchaError && (
                <p className={cn("error", "captcha")}>{captchaError}</p>
              )}
            </div>
          </div>

          <div className="contact_us-footer">
            <button onClick={handleRequestContactUs} className="form__button">
              {t("buttonSend")}
            </button>

            {responseError && (
              <p className={cn("error", "response")}>{responseError}</p>
            )}
          </div>
        </form>
      )}
    </>
  );
};
